// src/components/products/ProductDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchProduct,
  deleteProduct,
  toggleProductStatus,
  deleteFeature,
  deleteBenefit,
  deleteSection,
  deleteQA,
  deletePricingTier,
  deleteEligibility,
  deleteExclusion,
  clearProductError,
} from '../../store/actions/productActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';
import DocumentGallery from '../common/DocumentGallery';

const ProductDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { 
    selectedProduct: product, 
    loading, 
    error,
    features,
    benefits,
    sections,
    qas,
    pricingTiers,
    eligibilityCriteria,
    exclusions,
    documents,
  } = useSelector((state) => state.products);

  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showItemDeleteConfirm, setShowItemDeleteConfirm] = useState(false);
  const [deleteItemType, setDeleteItemType] = useState('');

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
      textDecoration: 'none',
    },
    editButton: {
      backgroundColor: '#4f46e5',
      color: '#ffffff',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    featuresButton: {
      backgroundColor: '#0891b2',
      color: '#ffffff',
    },
    featuresButtonHover: {
      backgroundColor: '#0e7490',
    },
    pricingButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
    },
    pricingButtonHover: {
      backgroundColor: '#047857',
    },
    toggleButton: {
      backgroundColor: '#f59e0b',
      color: '#ffffff',
    },
    toggleButtonHover: {
      backgroundColor: '#d97706',
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    hero: {
      padding: '2rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    heroContent: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'flex-start',
    },
    productIcon: {
      width: '100px',
      height: '100px',
      borderRadius: '0.5rem',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '3rem',
      color: '#4b5563',
    },
    productIconImage: {
      width: '100px',
      height: '100px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
    },
    productInfo: {
      flex: 1,
    },
    productName: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    productShortName: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    productMeta: {
      display: 'flex',
      gap: '2rem',
      flexWrap: 'wrap',
      marginTop: '1rem',
    },
    metaItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    metaLabel: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      color: '#6b7280',
      marginBottom: '0.25rem',
    },
    metaValue: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#111827',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.5rem 1rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    inactiveBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    popularBadge: {
      backgroundColor: '#fbbf24',
      color: '#92400e',
      marginLeft: '0.5rem',
    },
    newBadge: {
      backgroundColor: '#dbeafe',
      color: '#2563eb',
      marginLeft: '0.5rem',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1.5rem',
      marginTop: '2rem',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
    },
    tabContent: {
      padding: '2rem',
    },
    description: {
      fontSize: '1rem',
      lineHeight: '1.8',
      color: '#374151',
      marginBottom: '2rem',
    },
    section: {
      marginBottom: '2rem',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
    },
    addButton: {
      padding: '0.375rem 0.75rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '1rem',
    },
    card: {
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.5rem',
    },
    cardTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
    },
    cardSubtitle: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    cardContent: {
      fontSize: '0.875rem',
      color: '#374151',
      lineHeight: '1.6',
    },
    cardMeta: {
      display: 'flex',
      gap: '1rem',
      marginTop: '0.5rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    cardActions: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    iconButton: {
      padding: '0.25rem',
      background: 'none',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'all 0.2s',
    },
    pricingTier: {
      padding: '1rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
    },
    pricingHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
    },
    pricingName: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
    },
    pricingBadge: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#fbbf24',
      color: '#92400e',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    pricingAmount: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#059669',
      marginBottom: '0.5rem',
    },
    pricingCoverage: {
      fontSize: '0.875rem',
      color: '#374151',
      marginBottom: '0.5rem',
    },
    pricingFeatures: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    featureTag: {
      padding: '0.25rem 0.5rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
    },
    list: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    listItem: {
      padding: '0.5rem 0',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    listItemContent: {
      flex: 1,
    },
    listItemTitle: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    listItemSubtitle: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    statusModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    statusModalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '2rem',
      maxWidth: '400px',
      width: '90%',
    },
    statusModalTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    statusOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      marginBottom: '2rem',
    },
    statusOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    statusOptionSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#eff6ff',
    },
    statusOptionRadio: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchProduct(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (product) {
      setNewStatus(product.isActive ? 'active' : 'inactive');
    }
  }, [product]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteProduct(id));
      navigate('/admin/products');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const handleToggleStatus = useCallback(async () => {
    try {
      await dispatch(toggleProductStatus(id));
      setShowStatusModal(false);
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  }, [dispatch, id]);

  const handleItemDelete = useCallback(async () => {
    if (!itemToDelete) return;

    try {
      switch (deleteItemType) {
        case 'feature':
          await dispatch(deleteFeature(id, itemToDelete.id));
          break;
        case 'benefit':
          await dispatch(deleteBenefit(id, itemToDelete.id));
          break;
        case 'section':
          await dispatch(deleteSection(id, itemToDelete.id));
          break;
        case 'qa':
          await dispatch(deleteQA(id, itemToDelete.id));
          break;
        case 'pricing':
          await dispatch(deletePricingTier(id, itemToDelete.id));
          break;
        case 'eligibility':
          await dispatch(deleteEligibility(id, itemToDelete.id));
          break;
        case 'exclusion':
          await dispatch(deleteExclusion(id, itemToDelete.id));
          break;
        default:
          break;
      }
      setShowItemDeleteConfirm(false);
      setItemToDelete(null);
      setDeleteItemType('');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, itemToDelete, deleteItemType]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const tabs = [
    { id: 'overview', label: 'Aperçu' },
    { id: 'features', label: `Caractéristiques (${features.length})` },
    { id: 'benefits', label: `Avantages (${benefits.length})` },
    { id: 'pricing', label: `Tarifs (${pricingTiers.length})` },
    { id: 'eligibility', label: `Éligibilité (${eligibilityCriteria.length})` },
    { id: 'exclusions', label: `Exclusions (${exclusions.length})` },
    { id: 'sections', label: `Sections (${sections.length})` },
    { id: 'qa', label: `FAQ (${qas.length})` },
    { id: 'documents', label: `Documents (${documents.length})` },
  ];

  if (loading && !product) {
    return <LoadingSpinner fullScreen />;
  }

  if (!product && !loading) {
    return (
      <div style={styles.container}>
        <Alert
          type="error"
          message="Produit non trouvé"
        />
        <Link to="/admin/products" style={styles.backButton}>
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/products')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails du produit</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/products/${id}/features`}
            style={{
              ...styles.actionButton,
              ...styles.featuresButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.featuresButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.featuresButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Caractéristiques
          </Link>
          <Link
            to={`/admin/products/${id}/pricing`}
            style={{
              ...styles.actionButton,
              ...styles.pricingButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.pricingButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.pricingButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Tarifs
          </Link>
          <Link
            to={`/admin/products/${id}/edit`}
            style={{
              ...styles.actionButton,
              ...styles.editButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>
          <button
            onClick={() => setShowStatusModal(true)}
            style={{
              ...styles.actionButton,
              ...styles.toggleButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.toggleButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.toggleButton.backgroundColor;
            }}
          >
            {product.isActive ? (
              <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            ) : (
              <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
            )}
            {product.isActive ? 'Désactiver' : 'Activer'}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              ...styles.actionButton,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearProductError())}
        />
      )}

      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                style={styles.productIconImage}
              />
            ) : (
              <div style={styles.productIcon}>
                {product.icon || '📦'}
              </div>
            )}
            <div style={styles.productInfo}>
              <h2 style={styles.productName}>
                {product.name}
                {product.popular && (
                  <span style={{ ...styles.badge, ...styles.popularBadge }}>
                    ★ Populaire
                  </span>
                )}
                {product.new && (
                  <span style={{ ...styles.badge, ...styles.newBadge }}>
                    ✨ Nouveau
                  </span>
                )}
              </h2>
              {product.shortName && (
                <div style={styles.productShortName}>
                  {product.shortName}
                </div>
              )}
              <div style={styles.productMeta}>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Entité</span>
                  <span style={styles.metaValue}>
                    {product.entity?.name || '—'}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Statut</span>
                  <span
                    style={{
                      ...styles.badge,
                      ...(product.isActive ? styles.activeBadge : styles.inactiveBadge),
                    }}
                  >
                    {product.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Âge</span>
                  <span style={styles.metaValue}>
                    {product.minAge && product.maxAge ? (
                      `${product.minAge} - ${product.maxAge} ans`
                    ) : product.minAge ? (
                      `Dès ${product.minAge} ans`
                    ) : product.maxAge ? (
                      `Jusqu'à ${product.maxAge} ans`
                    ) : (
                      'Tous âges'
                    )}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Ordre</span>
                  <span style={styles.metaValue}>#{product.order || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Caractéristiques</div>
              <div style={styles.statValue}>{features.length}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Avantages</div>
              <div style={styles.statValue}>{benefits.length}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Tarifs</div>
              <div style={styles.statValue}>{pricingTiers.length}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Documents</div>
              <div style={styles.statValue}>{documents.length}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'overview' && (
            <div>
              {/* Description */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Description</h3>
                <div style={styles.description}>
                  {product.detailedDescription || product.description || 'Aucune description'}
                </div>
              </div>

              {/* Quick Stats */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Résumé</h3>
                <div style={styles.grid}>
                  <div style={styles.card}>
                    <div style={styles.cardHeader}>
                      <span style={styles.cardTitle}>Couleur</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '0.25rem',
                        backgroundColor: product.color || '#2563eb',
                      }} />
                      <span>{product.color || '#2563eb'}</span>
                    </div>
                  </div>
                  <div style={styles.card}>
                    <div style={styles.cardHeader}>
                      <span style={styles.cardTitle}>Icône</span>
                    </div>
                    <div style={{ fontSize: '2rem' }}>{product.icon || '📦'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Caractéristiques du produit</h3>
                <Link
                  to={`/admin/products/${id}/features`}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Gerer
                </Link>
              </div>
              {features.length > 0 ? (
                <div style={styles.grid}>
                  {features.map((feature) => (
                    <div key={feature.id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <span style={styles.cardTitle}>{feature.title}</span>
                        <div style={styles.cardActions}>
                          <Link
                            to={`/admin/products/${id}/features/${feature.id}/edit`}
                            style={styles.iconButton}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Link>
                          <button
                            style={styles.iconButton}
                            onClick={() => {
                              setItemToDelete(feature);
                              setDeleteItemType('feature');
                              setShowItemDeleteConfirm(true);
                            }}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div style={styles.cardContent}>{feature.description}</div>
                      <div style={styles.cardMeta}>
                        <span>Icône: {feature.icon || '—'}</span>
                        <span>Ordre: {feature.order || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune caractéristique définie
                </div>
              )}
            </div>
          )}

          {activeTab === 'benefits' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Avantages du produit</h3>
                <Link
                  to={`/admin/products/${id}/benefits`}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Gerer
                </Link>
              </div>
              {benefits.length > 0 ? (
                <div style={styles.grid}>
                  {benefits.map((benefit) => (
                    <div key={benefit.id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <span style={styles.cardTitle}>{benefit.title}</span>
                        <div style={styles.cardActions}>
                          <Link
                            to={`/admin/products/${id}/benefits/${benefit.id}/edit`}
                            style={styles.iconButton}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Link>
                          <button
                            style={styles.iconButton}
                            onClick={() => {
                              setItemToDelete(benefit);
                              setDeleteItemType('benefit');
                              setShowItemDeleteConfirm(true);
                            }}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div style={styles.cardContent}>{benefit.description}</div>
                      <div style={styles.cardMeta}>
                        <span>Montant: {benefit.amount || '—'}</span>
                        <span>Ordre: {benefit.order || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucun avantage défini
                </div>
              )}
            </div>
          )}

          {activeTab === 'pricing' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Tarifs</h3>
                <Link
                  to={`/admin/products/${id}/pricing`}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Gerer
                </Link>
              </div>
              {pricingTiers.length > 0 ? (
                <div>
                  {pricingTiers.map((tier) => (
                    <div key={tier.id} style={styles.pricingTier}>
                      <div style={styles.pricingHeader}>
                        <span style={styles.pricingName}>{tier.tierName}</span>
                        {tier.recommended && (
                          <span style={styles.pricingBadge}>Recommandé</span>
                        )}
                      </div>
                      <div style={styles.pricingAmount}>
                        {formatCurrency(tier.monthlyPremium)} <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>/mois</span>
                      </div>
                      <div style={styles.pricingCoverage}>
                        {tier.coverage}
                      </div>
                      {tier.features && tier.features.length > 0 && (
                        <div style={styles.pricingFeatures}>
                          {tier.features.map((feature, index) => (
                            <span key={index} style={styles.featureTag}>
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={styles.cardActions}>
                        <Link
                          to={`/admin/products/${id}/pricing/${tier.id}/edit`}
                          style={styles.iconButton}
                        >
                          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                        <button
                          style={styles.iconButton}
                          onClick={() => {
                            setItemToDelete(tier);
                            setDeleteItemType('pricing');
                            setShowItemDeleteConfirm(true);
                          }}
                        >
                          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucun tarif défini
                </div>
              )}
            </div>
          )}

          {activeTab === 'eligibility' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Critères d'éligibilité</h3>
                <Link
                  to={`/admin/products/${id}/eligibility`}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Gerer
                </Link>
              </div>
              {eligibilityCriteria.length > 0 ? (
                <ul style={styles.list}>
                  {eligibilityCriteria.map((criterion) => (
                    <li key={criterion.id} style={styles.listItem}>
                      <div style={styles.listItemContent}>
                        <div style={styles.listItemTitle}>{criterion.criterion}</div>
                      </div>
                      <div style={styles.cardActions}>
                        <Link
                          to={`/admin/products/${id}/eligibility/${criterion.id}/edit`}
                          style={styles.iconButton}
                        >
                          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                        <button
                          style={styles.iconButton}
                          onClick={() => {
                            setItemToDelete(criterion);
                            setDeleteItemType('eligibility');
                            setShowItemDeleteConfirm(true);
                          }}
                        >
                          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={styles.emptyState}>
                  Aucun critère d'éligibilité défini
                </div>
              )}
            </div>
          )}

          {activeTab === 'exclusions' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Exclusions</h3>
                <Link
                  to={`/admin/products/${id}/exclusions`}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Gerer
                </Link>
              </div>
              {exclusions.length > 0 ? (
                <ul style={styles.list}>
                  {exclusions.map((exclusion) => (
                    <li key={exclusion.id} style={styles.listItem}>
                      <div style={styles.listItemContent}>
                        <div style={styles.listItemTitle}>{exclusion.exclusion}</div>
                      </div>
                      <div style={styles.cardActions}>
                        <Link
                          to={`/admin/products/${id}/exclusions/${exclusion.id}/edit`}
                          style={styles.iconButton}
                        >
                          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                        <button
                          style={styles.iconButton}
                          onClick={() => {
                            setItemToDelete(exclusion);
                            setDeleteItemType('exclusion');
                            setShowItemDeleteConfirm(true);
                          }}
                        >
                          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={styles.emptyState}>
                  Aucune exclusion définie
                </div>
              )}
            </div>
          )}

          {activeTab === 'sections' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Sections supplémentaires</h3>
                <Link
                  to={`/admin/products/${id}/sections/add`}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Ajouter
                </Link>
              </div>
              {sections.length > 0 ? (
                <div style={styles.grid}>
                  {sections.map((section) => (
                    <div key={section.id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <span style={styles.cardTitle}>{section.title}</span>
                        <div style={styles.cardActions}>
                          <Link
                            to={`/admin/products/${id}/sections/${section.id}/edit`}
                            style={styles.iconButton}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Link>
                          <button
                            style={styles.iconButton}
                            onClick={() => {
                              setItemToDelete(section);
                              setDeleteItemType('section');
                              setShowItemDeleteConfirm(true);
                            }}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div style={styles.cardContent}>{section.content}</div>
                      <div style={styles.cardMeta}>
                        <span>Icône: {section.icon || '—'}</span>
                        <span>Ordre: {section.order || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune section définie
                </div>
              )}
            </div>
          )}

          {activeTab === 'qa' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Questions fréquentes</h3>
                <Link
                  to={`/admin/products/${id}/qa/add`}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Ajouter
                </Link>
              </div>
              {qas.length > 0 ? (
                <div style={styles.grid}>
                  {qas.map((qa) => (
                    <div key={qa.id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <span style={styles.cardTitle}>{qa.question}</span>
                        <div style={styles.cardActions}>
                          <Link
                            to={`/admin/products/${id}/qa/${qa.id}/edit`}
                            style={styles.iconButton}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Link>
                          <button
                            style={styles.iconButton}
                            onClick={() => {
                              setItemToDelete(qa);
                              setDeleteItemType('qa');
                              setShowItemDeleteConfirm(true);
                            }}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div style={styles.cardContent}>{qa.answer}</div>
                      <div style={styles.cardMeta}>
                        <span>Icône: {qa.icon || '—'}</span>
                        <span>Actif: {qa.isActive ? 'Oui' : 'Non'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune question fréquente définie
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div>
              <DocumentGallery
                documents={documents}
                entityId={id}
                entityType="product"
                onDelete={(doc) => {
                  setItemToDelete(doc);
                  setDeleteItemType('document');
                  setShowItemDeleteConfirm(true);
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div style={styles.statusModal} onClick={() => setShowStatusModal(false)}>
          <div style={styles.statusModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.statusModalTitle}>
              {product.isActive ? 'Désactiver le produit' : 'Activer le produit'}
            </h3>
            <p style={{ marginBottom: '2rem', color: '#374151' }}>
              {product.isActive 
                ? 'Êtes-vous sûr de vouloir désactiver ce produit ? Il ne sera plus visible.'
                : 'Êtes-vous sûr de vouloir activer ce produit ? Il sera visible par les clients.'}
            </p>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowStatusModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleToggleStatus}
                style={{
                  ...styles.saveButton,
                  backgroundColor: product.isActive ? '#dc2626' : '#059669',
                }}
              >
                {product.isActive ? 'Désactiver' : 'Activer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Delete Confirmation */}
      <ConfirmDialog
        isOpen={showItemDeleteConfirm}
        onClose={() => {
          setShowItemDeleteConfirm(false);
          setItemToDelete(null);
          setDeleteItemType('');
        }}
        onConfirm={handleItemDelete}
        title={`Supprimer ${deleteItemType === 'feature' ? 'la caractéristique' :
                deleteItemType === 'benefit' ? 'l\'avantage' :
                deleteItemType === 'section' ? 'la section' :
                deleteItemType === 'qa' ? 'la question' :
                deleteItemType === 'pricing' ? 'le tarif' :
                deleteItemType === 'eligibility' ? 'le critère' :
                deleteItemType === 'exclusion' ? 'l\'exclusion' :
                'le document'}`}
        message={`Êtes-vous sûr de vouloir supprimer cet élément ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Product Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer le produit"
        message={`Êtes-vous sûr de vouloir supprimer le produit "${product?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default ProductDetails;